import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import http from "@/api";
import { useApp } from "@/context";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const imgOf = (u) => (u && u.startsWith("/") ? `${API_BASE}${u}` : u);
const SRC = "home_rg_gifts";

/* 🎁 الهدايا من RG — صف دوائر أفقي يُدار بالكامل من لوحة الأدمن.
   لا يظهر إطلاقاً إذا لم يُضف الأدمن أي دائرة مفعّلة. */
export default function HomeGifts() {
  const { lang } = useApp();
  const ar = lang === "ar";
  const navigate = useNavigate();
  const [gifts, setGifts] = useState(null); // null = جارِ التحميل
  const sectionRef = useRef(null);
  const seenRef = useRef(false);

  useEffect(() => {
    http.get("/home-gifts")
      .then(({ data }) => setGifts(data?.gifts || []))
      .catch(() => setGifts([]));
  }, []);

  // إحصائية ظهور واحدة عند رؤية القسم فعلياً
  useEffect(() => {
    if (!gifts?.length || !sectionRef.current || seenRef.current) return;
    const ob = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !seenRef.current) {
        seenRef.current = true;
        http.post("/home-gifts/track", {
          event: "impression", gift_ids: gifts.map((g) => g.id), source: SRC,
        }).catch(() => {});
        ob.disconnect();
      }
    }, { threshold: 0.3 });
    ob.observe(sectionRef.current);
    return () => ob.disconnect();
  }, [gifts]);

  const open = (g) => {
    http.post("/home-gifts/track", { event: "click", gift_id: g.id, source: SRC }).catch(() => {});
    try {
      sessionStorage.setItem("rg_gifts_src", JSON.stringify({ ts: Date.now(), gift_id: g.id, source: SRC }));
    } catch {}
    const t = g.target_type;
    if (t === "department" && g.target_id) {
      navigate(`/shop?department=${encodeURIComponent(g.target_id)}&src=${SRC}`);
    } else if (t === "category" && g.target_id) {
      navigate(`/shop?category=${encodeURIComponent(g.target_id)}&src=${SRC}`);
    } else if (t === "product" && g.target_id) {
      navigate(`/p/${g.target_id}?src=${SRC}`);
    } else if (t === "link" && g.target_url) {
      if (/^https?:/i.test(g.target_url)) window.open(g.target_url, "_blank", "noopener");
      else navigate(`${g.target_url}${g.target_url.includes("?") ? "&" : "?"}src=${SRC}`);
    } else {
      navigate(`/shop?src=${SRC}`);
    }
  };

  // Skeleton أثناء التحميل فقط — وإذا لا توجد هدايا يختفي القسم كلياً
  if (gifts === null) {
    return (
      <div className="flex gap-3.5 overflow-hidden -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="home-gifts-skeleton">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="shrink-0 flex flex-col items-center gap-1.5" style={{ width: 86 }}>
            <div className="w-[76px] h-[76px] sm:w-[86px] sm:h-[86px] rounded-full skeleton" />
            <div className="w-14 h-2.5 rounded skeleton" />
          </div>
        ))}
      </div>
    );
  }
  if (!gifts.length) return null;

  return (
    <section ref={sectionRef} className="space-y-2.5" data-testid="home-gifts-section">
      <h2 className={`${ar ? "font-display-ar" : "font-display-en"} text-xl sm:text-2xl font-bold`}>
        {ar ? "🎁 الهدايا من RG" : "🎁 Gifts from RG"}
      </h2>
      <div className="flex gap-3.5 overflow-x-auto scroll-x-hide -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 snap-x">
        {gifts.map((g) => (
          <button key={g.id} onClick={() => open(g)}
                  className="shrink-0 snap-start flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
                  style={{ width: 90 }}
                  data-testid={`home-gift-circle-${g.id}`}>
            <span className="w-[76px] h-[76px] sm:w-[86px] sm:h-[86px] rounded-full overflow-hidden flex items-center justify-center bg-white"
                  style={{ border: "2px solid rgba(198,168,124,0.6)", boxShadow: "0 3px 12px -5px rgba(0,0,0,0.18)" }}>
              {g.image ? (
                <img src={imgOf(g.image)} alt={ar ? g.name_ar : g.name_en || g.name_ar}
                     loading="lazy" decoding="async"
                     className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl select-none">🎁</span>
              )}
            </span>
            <span className="text-[11.5px] font-bold leading-tight text-center"
                  style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {ar ? g.name_ar : g.name_en || g.name_ar}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}
